import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { 
  ArrowRight, 
  Database, 
  Settings, 
  GitBranch,
  Link,
  Layers,
  Upload,
  Download,
  AlertCircle
} from "lucide-react";

const DataVisualization = ({ entities, sources, targets, relationships, loading }) => {
  const [selectedView, setSelectedView] = useState("hierarchy");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredDeps, setHoveredDeps] = useState(null);

  const getEntityName = (entity) => {
    // Try to get entity name from sourceField first
    if (entity.sourceField && entity.sourceField.length > 0) {
      return entity.sourceField[0].entity || 'N/A';
    }
    // Fallback to entity field
    return entity.entity || 'N/A';
  };

  // Create hierarchy data for source systems
  const hierarchyData = useMemo(() => {
    const hierarchy = {};
    
    entities.forEach(entity => {
      if (!hierarchy[entity.source]) {
        hierarchy[entity.source] = {
          name: entity.source,
          targets: {},
          totalEntities: 0
        };
      }
      
      if (!hierarchy[entity.source].targets[entity.target]) {
        hierarchy[entity.source].targets[entity.target] = {
          name: entity.target,
          entities: [],
          dependencies: new Set(),
          dependentDetails: []
        };
      }
      
      hierarchy[entity.source].targets[entity.target].entities.push(entity);
      hierarchy[entity.source].totalEntities++;
      
      // Add dependencies with details
      if (entity.dependentTargets) {
        entity.dependentTargets.forEach(dep => {
          hierarchy[entity.source].targets[entity.target].dependencies.add(dep);
          // Find target details for dependency
          const targetDetail = targets.find(t => t.id === dep);
          if (targetDetail) {
            hierarchy[entity.source].targets[entity.target].dependentDetails.push({
              id: dep,
              name: targetDetail.name || `Target ${dep}`
            });
          }
        });
      }
    });

    return hierarchy;
  }, [entities, targets]);



  const HierarchyView = () => (
    <div className="space-y-6">
      {Object.values(hierarchyData).map((sourceSystem) => (
        <Card key={sourceSystem.name} className="card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Database className="w-5 h-5 text-blue-600" />
                <span>{sourceSystem.name}</span>
              </div>
              <Badge className="badge-info">
                {sourceSystem.totalEntities} entities
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(sourceSystem.targets).map((target) => (
                <div key={target.name} className="border rounded-lg p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <Settings className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-slate-800">{target.name}</span>
                    </div>
                    <Badge variant="outline">
                      {target.entities.length}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    {target.entities.map((entity) => (
                      <div key={entity.id} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                        <span className="text-slate-700 font-medium">{getEntityName(entity)}</span>
                        <div className="flex space-x-2">
                          {entity.dependentTargets && entity.dependentTargets.length > 0 && (
                            <div className="relative">
                              <Badge 
                                className="badge-warning text-xs cursor-pointer hover:bg-orange-200" 
                                onMouseEnter={() => setHoveredDeps({
                                  targetName: target.name,
                                  entityId: entity.id,
                                  dependencies: target.dependentDetails
                                })}
                                onMouseLeave={() => setHoveredDeps(null)}
                              >
                                {entity.dependentTargets.length} deps
                              </Badge>
                              
                              {hoveredDeps && hoveredDeps.entityId === entity.id && (
                                <div className="absolute bottom-full right-0 mb-2 z-10 bg-white border rounded-lg shadow-lg p-3 min-w-64">
                                  <h4 className="font-medium text-slate-800 mb-2">Dependencies</h4>
                                  <div className="space-y-1">
                                    {hoveredDeps.dependencies.map((dep, idx) => (
                                      <div key={idx} className="text-xs flex justify-between">
                                        <span>{dep.name}</span>
                                        <span className="text-slate-500">ID: {dep.id}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          {entity.incompatible && entity.incompatible.length > 0 && (
                            <Badge 
                              className="badge-error text-xs cursor-pointer hover:bg-red-200"
                              title={`Incompatible: ${entity.incompatible.join(', ')}`}
                            >
                              {entity.incompatible.length} conflicts
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {target.dependencies.size > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-slate-500 mb-1">All Dependencies:</p>
                      <div className="flex flex-wrap gap-1">
                        {Array.from(target.dependencies).slice(0, 3).map((dep, index) => {
                          const depDetail = target.dependentDetails.find(d => d.id === dep);
                          return (
                            <Badge key={index} className="badge-info text-xs">
                              {depDetail ? depDetail.name : dep}
                            </Badge>
                          );
                        })}
                        {target.dependencies.size > 3 && (
                          <span className="text-xs text-slate-400">
                            +{target.dependencies.size - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const StatsView = () => {
    const stats = useMemo(() => {
      const sourceCount = new Set(entities.map(e => e.source)).size;
      const targetCount = new Set(entities.map(e => e.target)).size;
      const entitiesWithDeps = entities.filter(e => e.dependentTargets && e.dependentTargets.length > 0).length;
      const entitiesWithIncompatibles = entities.filter(e => e.incompatible && e.incompatible.length > 0).length;
      
      return {
        sourceCount,
        targetCount,
        entitiesWithDeps,
        entitiesWithIncompatibles,
        totalConnections: entities.length,
        avgDependencies: entitiesWithDeps > 0 ? 
          entities.reduce((acc, e) => acc + (e.dependentTargets?.length || 0), 0) / entitiesWithDeps : 0
      };
    }, [entities]);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="card">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Upload className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.sourceCount}</p>
                <p className="text-sm text-slate-600">Source Systems</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Download className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.targetCount}</p>
                <p className="text-sm text-slate-600">Target Systems</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Link className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.totalConnections}</p>
                <p className="text-sm text-slate-600">Total Connections</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <GitBranch className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.entitiesWithDeps}</p>
                <p className="text-sm text-slate-600">With Dependencies</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.entitiesWithIncompatibles}</p>
                <p className="text-sm text-slate-600">With Incompatibles</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <Layers className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.avgDependencies.toFixed(1)}</p>
                <p className="text-sm text-slate-600">Avg Dependencies</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 rounded loading-skeleton"></div>
        <div className="h-96 bg-slate-200 rounded loading-skeleton"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">Data Visualization</h2>
        <Select value={selectedView} onValueChange={setSelectedView}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hierarchy">Hierarchy View</SelectItem>
            <SelectItem value="stats">Statistics</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selectedView === "hierarchy" && <HierarchyView />}
      {selectedView === "stats" && <StatsView />}
    </div>
  );
};

export default DataVisualization;